import {
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
} from "@100mslive/react-sdk";

/**
 * Media Session API handlers for the picture-in-picture window's own
 * microphone, camera and hang-up buttons.
 * https://web.dev/media-session/#video-conferencing-actions
 */
class SetupMediaSession {
  unsubscribers = [];

  /**
   * @param actions hmsActions
   * @param store the vanilla 100ms store
   * @param onLeave called after leaving from the PiP hang-up button, so the
   *   app can navigate to the leave page instead of waiting on a dead call
   */
  setup = (actions, store, onLeave) => {
    this.actions = actions;
    this.store = store;
    this.onLeave = onLeave;
    this.teardown();
    this.initState();
    this.setUpHandlers();
  };

  teardown = () => {
    this.unsubscribers.forEach(unsubscribe => unsubscribe());
    this.unsubscribers = [];
  };

  initState = () => {
    navigator.mediaSession?.setMicrophoneActive?.(
      this.store.getState(selectIsLocalAudioEnabled)
    );
    navigator.mediaSession?.setCameraActive?.(
      this.store.getState(selectIsLocalVideoEnabled)
    );

    this.unsubscribers.push(
      this.store.subscribe(isMicActive => {
        navigator.mediaSession?.setMicrophoneActive?.(isMicActive);
      }, selectIsLocalAudioEnabled),
      this.store.subscribe(isCamActive => {
        navigator.mediaSession?.setCameraActive?.(isCamActive);
      }, selectIsLocalVideoEnabled)
    );
  };

  toggleMic = async () => {
    const current = this.store.getState(selectIsLocalAudioEnabled);
    await this.actions.setLocalAudioEnabled(!current);
  };

  toggleCam = async () => {
    const current = this.store.getState(selectIsLocalVideoEnabled);
    await this.actions.setLocalVideoEnabled(!current);
  };

  leave = async () => {
    await this.actions.leave();
    this.onLeave?.();
  };

  setUpHandlers = () => {
    if (!navigator.mediaSession) {
      return;
    }
    try {
      navigator.mediaSession.setActionHandler(
        "togglemicrophone",
        this.toggleMic
      );
      navigator.mediaSession.setActionHandler("togglecamera", this.toggleCam);
      navigator.mediaSession.setActionHandler("hangup", this.leave);
    } catch (error) {
      console.error("error in setting media session handlers", error);
    }
  };
}

export const MediaSession = new SetupMediaSession();
